import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  CreateCheckoutSessionResponse,
  CreateSubscriptionForm,
  CustomerPortalFlow,
  CustomSubscriptionPlan,
  CustomSubscriptionPlanListResponse,
  SubscriptionPlanFilter,
  SubscriptionCustomerPortalSession,
  SubscriptionDetails,
  SubscriptionPlanMenu,
  TransactionHistory,
  UpdateSubscriptionPlanDetails,
  SwitchPlan,
  CursorPaginatedResponse,
  StartTrialPayload,
  ConnectionUser,
  ConnectionSubscriptionDetails,
  SubscriptionConnectionListItem,
} from '../models/subscription';
import { Observable } from 'rxjs';
import { PaginatedResponse } from '../../../shared/interfaces/pagination';

@Injectable({
  providedIn: 'root',
})
@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  private apiUrl = environment.apiBaseUrl;
  private http: HttpClient = inject(HttpClient);

  createCheckoutSession(
    plan: string,
    interval: string,
  ): Observable<CreateCheckoutSessionResponse> {
    return this.http.post<CreateCheckoutSessionResponse>(
      `${environment.apiBaseUrl}/api/subscription/checkout`,
      {
        plan,
        interval,
      },
    );
  }

  getSubscriptionDetails(): Observable<SubscriptionDetails> {
    return this.http.get<SubscriptionDetails>(
      `${this.apiUrl}/api/subscription/details`,
    );
  }

  getSubscriptionPlans(): Observable<SubscriptionPlanMenu[]> {
    return this.http.get<SubscriptionPlanMenu[]>(
      `${this.apiUrl}/api/subscription/plans`,
    );
  }

  cancelSubscription(): Observable<SubscriptionDetails> {
    return this.http.post<SubscriptionDetails>(
      `${environment.apiBaseUrl}/api/subscription/cancel`,
      {},
    );
  }

  switchPlan(payload: SwitchPlan): Observable<SubscriptionDetails> {
    return this.http.post<SubscriptionDetails>(
      `${environment.apiBaseUrl}/api/subscription/switch-plan`,
      payload,
    );
  }

  getTransactionHistory(
    pageSize: number,
    startingAfter?: string,
    endingBefore?: string,
  ): Observable<CursorPaginatedResponse<TransactionHistory>> {
    let params = new HttpParams();
    params = params.set('page_size', pageSize.toString());
    if (startingAfter) {
      params = params.set('starting_after', startingAfter);
    }
    if (endingBefore) {
      params = params.set('ending_before', endingBefore);
    }
    return this.http.get<CursorPaginatedResponse<TransactionHistory>>(
      `${environment.apiBaseUrl}/api/subscription/transactions`,
      {
        params,
      },
    );
  }

  redirectToCustomerPortal(
    flow: CustomerPortalFlow,
  ): Observable<SubscriptionCustomerPortalSession> {
    return this.http.post<SubscriptionCustomerPortalSession>(
      `${environment.apiBaseUrl}/api/subscription/customer-portal`,
      { flow },
    );
  }

  getSubscriptionAdmins(): Observable<ConnectionUser[]> {
    return this.http.get<ConnectionUser[]>(
      `${environment.apiBaseUrl}/api/subscription/admins`,
    );
  }

  getCustomsubscriptionPlans(
    page: number,
    pageSize: number,
    search?: string,
    status: SubscriptionPlanFilter = 'all',
  ): Observable<CustomSubscriptionPlanListResponse> {
    let params = new HttpParams();
    params = params.set('page', page.toString());
    params = params.set('page_size', pageSize.toString());
    params = params.set('status', status);
    const trimmedSearch = search?.trim();
    if (trimmedSearch) {
      params = params.set('search', trimmedSearch);
    }
    return this.http.get<CustomSubscriptionPlanListResponse>(
      `${environment.apiBaseUrl}/api/admin/subscription/plans`,
      {
        params,
      },
    );
  }

  getSubscriptionPlanDetail(
    planName: string,
  ): Observable<CustomSubscriptionPlan> {
    return this.http.get<CustomSubscriptionPlan>(
      `${environment.apiBaseUrl}/api/admin/subscription/${planName}/details`,
    );
  }

  createCustomSubscriptionPlan(
    payload: CreateSubscriptionForm,
  ): Observable<CreateSubscriptionForm> {
    return this.http.post<CreateSubscriptionForm>(
      `${environment.apiBaseUrl}/api/admin/subscription/plans`,
      payload,
    );
  }

  updateCustomSubscriptionPlan(
    payload: UpdateSubscriptionPlanDetails,
    plan_name: string,
  ): Observable<UpdateSubscriptionPlanDetails> {
    return this.http.patch<UpdateSubscriptionPlanDetails>(
      `${environment.apiBaseUrl}/api/admin/subscription/${plan_name}/details`,
      payload,
    );
  }

  startTrial(payload: StartTrialPayload): Observable<SubscriptionDetails> {
    return this.http.post<SubscriptionDetails>(
      `${environment.apiBaseUrl}/api/admin/subscription/trial`,
      payload,
    );
  }

  getSubscriptionConnections(
    page: number,
    pageSize: number,
  ): Observable<PaginatedResponse<SubscriptionConnectionListItem>> {
    let params = new HttpParams();
    params = params.set('page', page.toString());
    params = params.set('page_size', pageSize.toString());
    return this.http.get<PaginatedResponse<SubscriptionConnectionListItem>>(
      `${environment.apiBaseUrl}/api/admin/subscription/connections`,
      { params },
    );
  }

  getConnectionUsers(connectionName: string): Observable<ConnectionUser[]> {
    return this.http.get<ConnectionUser[]>(
      `${environment.apiBaseUrl}/api/admin/subscription/connection/${connectionName}/users`,
    );
  }

  getConnectionSubscriptionDetails(
    connectionName: string,
  ): Observable<ConnectionSubscriptionDetails> {
    return this.http.get<ConnectionSubscriptionDetails>(
      `${this.apiUrl}/api/admin/subscription/connection/${connectionName}/subscription`,
    );
  }
}
